import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import CadenceTable from '../../components/common/CadenceTable';
import { formatDate } from '../../helpers/data-format';
import Resources from '../Projects/sections/Project/sections/ProjectBoxComponent/Resources';
import Progress from '../Projects/sections/Project/sections/Progress';
import { FiAlertTriangle, FiEdit3, FiTrash2 } from 'react-icons/fi';
import { HiDotsVertical } from 'react-icons/hi';
import { getDashboardStatusColor, getDashboardStatusLabel } from "../../constants/integrationTypes";
import CustomTooltip from '../../components/common/CustomTooltip';
import TruncatedText from '../../components/common/TruncatedText';
import { useResourceInfo } from '../../hooks/useResourceInfo';
import { getPriorityColorClasses } from '../../utils/taskUtils';
import ProjectEmptyStateModal from './ProjectEmptyStateModal';
import {
  StatusCell,
  TaskTypeCell,
  TitleCell,
  IntegrationTypeCell,
  IntegrationStatusCell,
  EstimateCell,
  ProgressCell,
  DurationCell,
  DateCell,
  PriorityCell,
  SkillCell,
  DependencyCell,
  IdCell,
  ViewTitleSearchValue
} from '../../shared/molecules/CadenceTable/CadenceTable';
import CadenceDataTable from '../../shared/molecules/CadenceDataTable/CadenceDataTable';
import Portal from '../../components/Portal';
import TaskDeleteModal from '../../components/Modals/TaskDeleteModal';
import { useProjectService } from '../../services';

const defaultTaskTableVisible = {
  status: true,
  id: false,
  type: true,
  integration_item_type: true,
  name: true,
  dependency: true,
  duration: true,
  member: true,
  skill: true,
  adj_start_date: true,
  progress: true,
  delayed_date: true,
  priority: true,
  story_points: true,
  original_estimate: true,
  completed_story_points: false,
  completed_original_estimate: false,
  board_column: true
};

/** Task-level action menu with Edit and Delete options */
const TaskItemOptions: React.FC<{
  task: any;
  sim_flag: boolean;
  onEdit: (task: any) => void;
  onTaskUpdate?: () => void;
}> = ({ task, sim_flag, onEdit, onTaskUpdate }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { deleteTask } = useProjectService();

  useEffect(() => {
    if (showMenu && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownWidth = 160;
      const dropdownHeight = 88; // 2 items * 36px + 16px padding
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const shouldPositionAbove = (viewportHeight - rect.bottom) < dropdownHeight && rect.top > dropdownHeight;

      let leftPos = rect.right - dropdownWidth;
      if (leftPos < 8) leftPos = 8;
      if (leftPos + dropdownWidth > viewportWidth - 8) leftPos = viewportWidth - dropdownWidth - 8;

      setDropdownPosition({
        top: shouldPositionAbove ? rect.top - dropdownHeight : rect.bottom + 2,
        left: leftPos,
      });
    }
  }, [showMenu]);

  useEffect(() => {
    if (!showMenu) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (buttonRef.current && !buttonRef.current.contains(target) &&
          (!dropdownRef.current || !dropdownRef.current.contains(target))) {
        setShowMenu(false);
      }
    };
    const handleDismiss = () => setShowMenu(false);
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleDismiss, true);
    window.addEventListener('resize', handleDismiss);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleDismiss, true);
      window.removeEventListener('resize', handleDismiss);
    };
  }, [showMenu]);

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMenu(false);
    onEdit(task);
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMenu(false);
    setShowDeleteConfirm(true);
  };

  const submitDelete = async () => {
    const item_id = task.item_id || task.id;
    const project_id = task.project_id;
    if (item_id && project_id) {
      setIsDeleting(true);
      try {
        await deleteTask(project_id, item_id, sim_flag);
        if (onTaskUpdate) onTaskUpdate();
      } catch (error) {
        console.error('Error deleting task:', error);
      } finally {
        setIsDeleting(false);
      }
    }
    setShowDeleteConfirm(false);
  };

  return (
    <>
      <div className="px-4 py-2 font-medium relative">
        <button
          ref={buttonRef}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowMenu(true); }}
          type="button"
          className="inline-flex items-center justify-center w-8 h-8 rounded-full cursor-pointer text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
          title="More actions"
        >
          <HiDotsVertical className="w-4 h-4" />
        </button>

        {showMenu && (
          <Portal>
            <div
              ref={dropdownRef}
              className="fixed min-w-[160px] rounded-lg border border-light-border-default dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xl overflow-hidden z-[99999]"
              style={{ top: `${dropdownPosition.top}px`, left: `${dropdownPosition.left}px` }}
            >
              <div className="py-2">
                <button
                  onClick={handleEdit}
                  className="w-full text-left flex items-center gap-2 px-3 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-150"
                >
                  <FiEdit3 className="w-4 h-4" />
                  <span className="text-left leading-tight">Edit</span>
                </button>
                <button
                  onClick={handleDeleteClick}
                  className="w-full text-left flex items-center gap-2 px-3 py-2.5 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/20 transition-colors duration-150"
                >
                  <FiTrash2 className="w-4 h-4" />
                  <span className="text-left leading-tight">Delete</span>
                </button>
              </div>
            </div>
          </Portal>
        )}
      </div>
      <Portal>
        <TaskDeleteModal
          showModal={showDeleteConfirm}
          taskTitle={task?.task_title || task?.name}
          taskId={task?.item_id || task?.id}
          setShowModal={setShowDeleteConfirm}
          handleConfirm={submitDelete}
          isDeleting={isDeleting}
        />
      </Portal>
    </>
  );
};

interface ProjectListViewProps {
  tasks: any[];
  isDataApiLoading: boolean;
  sim_flag: boolean;
  hasSimulationTasks: boolean;
  readOnly: boolean;
  handleTaskClick: (task: any) => void;
  onToggleSim?: () => void;
  onMerge?: () => void;
  projectData: any;
  onTaskUpdate?: () => void;
}

const ProjectListView: React.FC<ProjectListViewProps> = ({
  tasks,
  projectData,
  isDataApiLoading,
  sim_flag,
  hasSimulationTasks,
  readOnly,
  handleTaskClick,
  onToggleSim,
  onMerge,
  onTaskUpdate,
}) => {
  const { getResourceName } = useResourceInfo();
  const [highlightedTaskId, setHighlightedTaskId] = useState<string | null>(null);
  const hasIntegrationType = Array.isArray(tasks) && tasks.some((task) => task.integration_data);
  const { template_id } = useSelector((state: any) => state.templates);
  const isTemplate = !!template_id;

  useEffect(() => {
    const handler = (e: any) => {
      if (!tasks || tasks.length === 0) return;
      const detail = e?.detail || {};
      const rawTaskId = String(detail.taskId || '').trim();
      if (!rawTaskId) return;

      const list = tasks.slice(1);
      const found = list.find((t: any) => String(t.id) === rawTaskId || String(t.item_id) === rawTaskId);
      if (!found) return;

      setTimeout(() => {
        const rowElement = document.querySelector(`[data-task-id="${found.id || found.item_id}"]`);
        if (rowElement) {
          rowElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);

      setHighlightedTaskId(found.id || found.item_id);
      setTimeout(() => {
        setHighlightedTaskId(null);
      }, 4500);
    };

    window.addEventListener('cadencepro:locate-task', handler as EventListener);
    return () => window.removeEventListener('cadencepro:locate-task', handler as EventListener);
  }, [tasks]);
  const columns = [
    ...(!isTemplate
      ? [
          {
            key: "name",
            label: "Title",
            searchValue: (task: any) => ViewTitleSearchValue(task),
            render: (task: any) => <TitleCell row={task} />,
          },

          {
            key: "status",
            label: "Status",
            searchValue: (task: any) =>
              getDashboardStatusLabel(task.status || ""),
            render: (row: any) => <StatusCell row={row} />,
          },
        ]
      : []),
    {
      key: "progress",
      label: "Progress",
      searchValue: (task: any) => task.progress,
      render: (task: any) => (
        <div className="flex justify-start">
          <ProgressCell progress={task.progress} />
        </div>
      ),
    },
    ...(hasIntegrationType
      ? [
          {
            key: "integration_status",
            label: "Integration Status",
            searchValue: (task: any) =>
              task.integration_data?.integration_status,
            render: (task: any) => (
              <IntegrationStatusCell
                status={task.integration_data?.integration_status}
              />
            ),
            sortValue: (project: any) =>
              (
                project.integration_data?.integration_status || ""
              ).toLowerCase(),
          },
          {
            key: "integration_type",
            label: "Integration Type",
            searchValue: (task: any) =>
              task.integration_data?.integration_item_type,
            render: (task: any) => (
              <IntegrationTypeCell
                type={task.integration_data?.integration_item_type}
              />
            ),
            sortValue: (task: any) =>
              (
                task.integration_data?.integration_item_type ?? ""
              ).toLowerCase(),
          },
          {
            key: "board_column",
            label: "Board Column",
            searchValue: (task: any) =>
              task.integration_data?.integration_tags?.board_column || "",
            render: (task: any) => {
              const boardColumn =
                task.integration_data?.integration_tags?.board_column;
              return (
                <div className="px-4 py-2 text-sm text-gray-700 dark:text-gray-200">
                  {boardColumn || "—"}
                </div>
              );
            },
            sortValue: (task: any) =>
              (
                task.integration_data?.integration_tags?.board_column ?? ""
              ).toLowerCase(),
          },
        ]
      : []),

    {
      key: "duration",
      label: "Duration (In Days)",
      searchValue: (task: any) => task.duration,
      headerRender: () => (
        <span>
          Duration <br /> (In Days)
        </span>
      ),
      render: (task: any) => <DurationCell value={task.duration} />,
      sortValue: (task: any) => task.duration,
    },

    {
      key: "delayed",
      label: "Delayed (In Days)",
      searchValue: (task: any) => task.delayed_duration,
      headerRender: () => (
        <span>
          Delayed <br /> (In Days)
        </span>
      ),
      render: (task: any) => <DurationCell value={task.delayed_duration} />,
      sortValue: (task: any) => task.delayed_duration,
    },

    ...(hasIntegrationType
      ? [
          {
            key: "story_points",
            label: "Story Points",
            searchValue: (task: any) => {
              const integrationType =
                task.integration_data?.integration_item_type;
              const storyPoints =
                task.integration_data?.integration_tags?.story_points;
              if (
                ["user story", "bug", "spike"].includes(
                  integrationType?.toLowerCase() || "",
                )
              ) {
                return storyPoints;
              }
              return "";
            },
            render: (task: any) => {
              const integrationType =
                task.integration_data?.integration_item_type;
              const storyPoints =
                task.integration_data?.integration_tags?.story_points;
              const storyPointsComputed =
                task.integration_data?.integration_tags?.story_points_computed;

              if (
                ["user story", "bug", "spike"].includes(
                  integrationType?.toLowerCase() || "",
                )
              ) {
                return (
                  <EstimateCell
                    value={storyPoints}
                    isComputed={storyPointsComputed}
                    tooltipTitle="Story points computed by Cadence Pro"
                  />
                );
              }
              return <EstimateCell value={undefined} />;
            },
            sortValue: (task: any) => {
              const integrationType =
                task.integration_data?.integration_item_type;
              const storyPoints =
                task.integration_data?.integration_tags?.story_points;

              if (
                ["user story", "bug", "spike"].includes(
                  integrationType?.toLowerCase() || "",
                )
              ) {
                return storyPoints !== undefined && storyPoints !== null
                  ? storyPoints
                  : 0;
              }
              return 0;
            },
          },
        ]
      : []),
    ...(hasIntegrationType
      ? [
          {
            key: "completed_story_points",
            label: "Actual Story Points",
            searchValue: (task: any) => {
              const integrationType =
                task.integration_data?.integration_item_type;
              const completedStoryPoints =
                task.integration_data?.integration_tags?.completed_story_points;
              if (
                ["user story", "bug", "spike"].includes(
                  integrationType?.toLowerCase() || "",
                )
              ) {
                return completedStoryPoints;
              }
              return "";
            },
            render: (task: any) => {
              const integrationType =
                task.integration_data?.integration_item_type;
              const completedStoryPoints =
                task.integration_data?.integration_tags?.completed_story_points;
              const completedStoryPointsComputed =
                task.integration_data?.integration_tags
                  ?.completed_story_points_computed;

              if (
                ["user story", "bug", "spike"].includes(
                  integrationType?.toLowerCase() || "",
                )
              ) {
                return (
                  <EstimateCell
                    value={completedStoryPoints}
                    isComputed={completedStoryPointsComputed}
                    isCompleted={true}
                    tooltipTitle="Actual story points computed by Cadence Pro"
                  />
                );
              }
              return <EstimateCell value={undefined} />;
            },
            sortValue: (task: any) => {
              const integrationType =
                task.integration_data?.integration_item_type;
              const completedStoryPoints =
                task.integration_data?.integration_tags?.completed_story_points;

              if (
                ["user story", "bug", "spike"].includes(
                  integrationType?.toLowerCase() || "",
                )
              ) {
                return completedStoryPoints !== undefined &&
                  completedStoryPoints !== null
                  ? completedStoryPoints
                  : 0;
              }
              return 0;
            },
          },
        ]
      : []),
    ...(hasIntegrationType
      ? [
          {
            key: "original_estimate",
            label: "Original Estimate",
            searchValue: (task: any) => {
              const integrationType =
                task.integration_data?.integration_item_type;
              const originalEstimate =
                task.integration_data?.integration_tags?.original_estimate;
              if (integrationType?.toLowerCase() === "task") {
                return originalEstimate;
              }
              return "";
            },
            render: (task: any) => {
              const integrationType =
                task.integration_data?.integration_item_type;
              const originalEstimate =
                task.integration_data?.integration_tags?.original_estimate;
              const originalEstimateComputed =
                task.integration_data?.integration_tags
                  ?.original_estimate_computed;

              if (integrationType?.toLowerCase() === "task") {
                return (
                  <EstimateCell
                    value={originalEstimate}
                    isComputed={originalEstimateComputed}
                    tooltipTitle="Original estimate computed by Cadence Pro"
                  />
                );
              }
              return <EstimateCell value={undefined} />;
            },
            sortValue: (task: any) => {
              const integrationType =
                task.integration_data?.integration_item_type;
              const originalEstimate =
                task.integration_data?.integration_tags?.original_estimate;

              if (integrationType?.toLowerCase() === "task") {
                return originalEstimate !== undefined &&
                  originalEstimate !== null
                  ? parseFloat(originalEstimate)
                  : 0;
              }
              return 0;
            },
          },
        ]
      : []),
    ...(hasIntegrationType
      ? [
          {
            key: "completed_original_estimate",
            label: "Completed Estimate",
            searchValue: (task: any) => {
              const integrationType =
                task.integration_data?.integration_item_type;
              const completedOriginalEstimate =
                task.integration_data?.integration_tags
                  ?.completed_original_estimate;
              if (integrationType?.toLowerCase() === "task") {
                return completedOriginalEstimate;
              }
              return "";
            },
            render: (task: any) => {
              const integrationType =
                task.integration_data?.integration_item_type;
              const completedOriginalEstimate =
                task.integration_data?.integration_tags
                  ?.completed_original_estimate;
              const completedOriginalEstimateComputed =
                task.integration_data?.integration_tags
                  ?.completed_original_estimate_computed;

              if (integrationType?.toLowerCase() === "task") {
                return (
                  <EstimateCell
                    value={completedOriginalEstimate}
                    isComputed={completedOriginalEstimateComputed}
                    isCompleted={true}
                    tooltipTitle="Completed estimate computed by Cadence Pro"
                  />
                );
              }
              return <EstimateCell value={undefined} />;
            },
            sortValue: (task: any) => {
              const integrationType =
                task.integration_data?.integration_item_type;
              const completedOriginalEstimate =
                task.integration_data?.integration_tags
                  ?.completed_original_estimate;

              if (integrationType?.toLowerCase() === "task") {
                return completedOriginalEstimate !== undefined &&
                  completedOriginalEstimate !== null
                  ? parseFloat(completedOriginalEstimate)
                  : 0;
              }
              return 0;
            },
          },
        ]
      : []),

    {
      key: "due_date",
      label: "Due Date",
      searchValue: (task: any) => task?.due_date,
      render: (task: any) => <DateCell date={task?.due_date} />,
    },
    {
      key: "priority",
      label: "Priority",
      searchValue: (task: any) => task?.priority || "",
      render: (task: any) => <PriorityCell priority={task?.priority} />,
      sortValue: (task: any) => task?.priority || "",
    },
    {
      key: "skill",
      label: "Skill",
      searchValue: (task: any) => task.skill,
      render: (task: any) => <SkillCell skill={task.skill} />,
    },
    {
      key: "dependency",
      label: "Block By",
      searchValue: (task: any) => {
        const dependencies =
          Array.isArray(task.dependencies) && task.dependencies.length > 0
            ? task.dependencies.join(", ")
            : "";
        return dependencies;
      },
      render: (task: any) => (
        <DependencyCell dependencies={task.dependencies} />
      ),
    },
    // Hide members column for templates
    ...(!isTemplate
      ? [
          {
            key: "member",
            label: "Members",
            searchValue: (task: any) => getResourceName(task.responsible),
            render: (task: any) => (
              <Resources
                resources={task.responsible ? [task.responsible] : []}
                projectId={task.project_id}
              />
            ),
          },
        ]
      : []),
    {
      key: "adj_start_date",
      label: "Start Date",
      searchValue: (task: any) => task?.adj_start_date,
      render: (task: any) => <DateCell date={task?.adj_start_date} />,
    },
    {
      key: "id",
      label: "ID",
      searchValue: (task: any) => task.id,
      render: (task: any) => <IdCell id={task.id} />,
      sortValue: (task: any) => {
        // Extract numeric part from alphanumerical id (e.g., 'task-10' -> 10)
        if (typeof task.id === "string") {
          const match = task.id.match(/(\d+)$/);
          if (match) {
            return parseInt(match[1], 10);
          }
        }
        // Fallback: sort by string value
        return task.id;
      },
    },
    {
      key: "action",
      label: "",
      sortable: false,
      skeletonShape: "action",
      render: (task: any) => (
        <TaskItemOptions
          task={task}
          sim_flag={sim_flag}
          onEdit={handleTaskClick}
          onTaskUpdate={onTaskUpdate}
        />
      ),
    },
  ];
  // Determine if we should show the empty state modal
  const hasNoTasks = !tasks || tasks.length <= 1;
  const showEmptyStateModal = hasNoTasks && !isDataApiLoading;

  let emptyContent: React.ReactNode;
  if (showEmptyStateModal) {
    // Show the ProjectEmptyStateModal inside the table's empty state
    emptyContent = (
      <div className="flex justify-center items-center h-[50vh] w-full">
        <ProjectEmptyStateModal
          showModal={showEmptyStateModal}
          entityType="tasks"
          simFlag={sim_flag}
          hasSimulationTasks={hasSimulationTasks}
          onToggleSim={onToggleSim}
          onMerge={onMerge}
        />
      </div>
    );
  } else {
    // Fallback to simple text messages if needed
    if (!sim_flag && hasSimulationTasks) {
      emptyContent = (
        <div className="text-center">
          <p>You currently have no task data in live mode.</p>
          <p className="font-semibold text-primary-light dark:text-primary-dark">Tasks are available in simulation mode. Please toggle to simulation mode to view them.</p>
        </div>
      );
    } else if (!sim_flag) {
      emptyContent = (
        <div className="text-center">
          <p>You currently have no task data in live mode. Please create a new task to begin.</p>
        </div>
      );
    } else {
      emptyContent = (
        <div className="text-center">
          You currently have no task data in simulation mode. Please create a new task to begin.
        </div>
      );
    }
  }

  return (
    <div className="flex-1 min-h-0 px-4 pb-[50px]">
      <CadenceDataTable
        columns={columns}
        data={tasks ? tasks.slice(1) : []}
        loading={isDataApiLoading}
        defaultVisible={((): Record<string, boolean> => {
          const base: Record<string, boolean> = { ...defaultTaskTableVisible };
          if (isTemplate) {
            base['status'] = false;
            base['member'] = false;
          }
          // Ensure integration keys exist in defaults so they can be toggled
          if (typeof base['integration_type'] === 'undefined') base['integration_type'] = true;
          if (typeof base['integration_status'] === 'undefined') base['integration_status'] = true;
          if (typeof base['story_points'] === 'undefined') base['story_points'] = true;
          if (typeof base['original_estimate'] === 'undefined') base['original_estimate'] = true;
          if (typeof base['completed_story_points'] === 'undefined') base['completed_story_points'] = false;
          if (typeof base['completed_original_estimate'] === 'undefined') base['completed_original_estimate'] = false;
          if (typeof base['board_column'] === 'undefined') base['board_column'] = true;
          const hasIntegration = Array.isArray(tasks) && tasks.some((t: any) => !!t?.integration_data);
          if (!hasIntegration) {
            base['integration_type'] = false;
            base['integration_status'] = false;
            base['story_points'] = false;
            base['original_estimate'] = false;
            base['completed_story_points'] = false;
            base['completed_original_estimate'] = false;
            base['board_column'] = false;
          }
          return base;
        })()}
        columnVisibilityKey={`project_level_table`}
        onRowClick={!readOnly ? handleTaskClick : undefined}
        highlightedRowId={highlightedTaskId}
        columnWeights={{
          name: 22,
          status: 10,
          progress: 8,
          integration_status: 10,
          integration_type: 10,
          board_column: 9,
          duration: 8,
          delayed: 8,
          story_points: 8,
          completed_story_points: 8,
          original_estimate: 8,
          completed_original_estimate: 8,
          due_date: 10,
          priority: 7,
          skill: 8,
          dependency: 10,
          member: 10,
          adj_start_date: 10,
          id: 8,
          action: 6,
        }}
        emptyMessage={
          <div className="dark:text-white text-gray-800 flex justify-center items-center h-[50vh]">
            {emptyContent}
          </div>
        }
      />
    </div>
  );
};

export default ProjectListView;
